import { IUserGroup } from './users.interface';
import { ICountry, ICountryDataServer } from './country.interface';
import { IBaseDepartment, IDepartment } from './departments.interface';
import { IAdminUser, IAdminUserServer } from './admin-users.interface';

import { IPAccessControlModeType, UsergroupsInclusionsValues } from '@admin/settings/util';

export enum SocialConnectMode {
  REQUIRED = 'required',
  OPTIONAL = 'optional',
  HIDDEN = 'hidden'
}

export interface IDomainServer {
  domain_name: string;
  include_subdomains: boolean;
}

export type usergroup_restriction_server = null | 'include' | 'exclude';

export interface IPageSettingsServer {
  language: string;
  teaser_text: string;
  confirmation_text: string;
  parsed_teaser_text?: string;
}

export interface IComplianceServer {
  created_at: string;
  updated_at: string;
  compliance_id: number;
  approval_required: number;
  compliance?: {
    title: string;
    contents: string;
    created_at: string;
    updated_at: string;
  };
}

export interface ISelfSignupSettingsServer {
  id: number;
  created_at: string;
  signup_url: string;
  hide_department?: boolean | number;
  allow_signup: number;
  sms_verification: number;
  usergroups: IUserGroup[];
  email_verification: number;
  admins: IAdminUserServer[];
  send_welcome_email: number;
  send_thankyou_email: number;
  admin_approval_required: number;
  default_department?: IDepartment;
  allowed_domains: IDomainServer[];
  page_settings: IPageSettingsServer;
  allow_usergroups_selection: number;
  country_selection_required: number;
  filter_usergroups_by_country: number;
  signup_compliances: IComplianceServer[];
  email_digest_admin_ids: IAdminUserServer[];
  approver_custom_selection?: number;
  approvers?: IAdminUser[],
  usergroup_restriction?: usergroup_restriction_server;
  usergroup_restriction_selection?: IUserGroup[];
  client_country?: ICountryDataServer;

  // IP Access Control
  ip_blocked_message: string;
  ip_access_control_enabled: boolean | number;
  ip_rules: { id: number, ip: string }[];
  ip_access_control_mode: IPAccessControlModeType;

  social_connect_settings: ISelfSignupSocialConnectSettingsServer;
}

export interface IDomain {
  id?: string;
  title?: string;
  domainName: string;
  includeSubdomains?: boolean;
}

export interface IPageSettings {
  language: string;
  teaserText: string;
  confirmationText: string;
  parsedTeaserText: string;
}

export interface ICompliance {
  createdAt?: string;
  updatedAt?: string;
  id?: number | string;
  approvalRequired: boolean;
  isNewCompliance?: boolean;
  compliance?: {
    title: string;
    contents: string;
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface ISelfSignupSettings {
  id: number;
  createdAt: string;
  signupURL: string;
  allowSignup: boolean;
  adminIDs: IAdminUser[];
  smsVerification: boolean;
  usergroups: IUserGroup[];
  allowedDomains: IDomain[];
  sendWelcomeEmail: boolean;
  sendThankyouEmail: boolean;
  hideDeptSelection: boolean;
  emailVerification: boolean;
  pageSettings: IPageSettings;
  adminApprovalRequired: boolean;
  signupCompliances: ICompliance[];
  usergroupRestriction?: UsergroupsInclusionsValues;
  usergroupRestrictionIds?: IUserGroup[];
  approverCustomSelection?: number;
  approverAdmins?: IAdminUser[],
  allowUsergroupsSelection: boolean;
  countrySelectionRequired: boolean;
  filterUsergroupsByCountry: boolean;
  emailDigestAdminIDs: IAdminUser[];
  defaultDepartment?: IDepartment;
  clientCountry?: ICountry;

  // IP Access Control
  ipAddresses: string[];
  customMessage: string;
  ipAccessControlEnabled: boolean;
  ipAccessControlMode: IPAccessControlModeType;

  socialConnectSettings: ISelfSignupSocialConnectSettings;
}

export interface ICompliancePayload {
  id?: number | string;
  title: string;
  contents: string;
  approval_required: boolean;
}

export interface ISelfSignupPayload {
  id?: number | string;
  admin_ids: number[];
  allow_signup: boolean;
  usergroup_ids: number[];
  captcha_enable?: boolean;
  sms_verification: boolean;
  email_verification: boolean;
  send_welcome_email: boolean;
  send_thankyou_email: boolean;
  hide_department: boolean;
  default_department_id: number;
  admin_approval_required: boolean;
  allowed_domains: IDomainServer[];
  email_digest_admin_ids: number[];
  usergroup_restriction?: usergroup_restriction_server;
  usergroup_restriction_ids?: number[];
  compliances: ICompliancePayload[];
  page_settings: IPageSettingsServer;
  allow_usergroups_selection: boolean;
  country_selection_required: boolean;
  filter_usergroups_by_country: boolean;
  approver_custom_selection?: boolean;
  approver_ids?: number[],
}

export interface ISignupStatusPayload {
  action?: string;
  action_token: string;
}

export interface ISignupStatusServer {
  action: string;
  user_id: number;
  message: string;
  redirect_to: string;
  usergroups: IUserGroup[];
  current_user_state: string;
  department: IBaseDepartment;
}

export interface ISignupStatus {
  action: string;
  userId: number;
  message: string;
  redirectTo: string;
  currentUserState: string;
  usergroups: IUserGroup[];
  department: IBaseDepartment;
}

export interface ISelfSignupIPRestrictionPayload {
  ip_rules: string[];
  ip_blocked_message: string;
  ip_access_control_enabled: boolean;
  ip_access_control_mode: IPAccessControlModeType;
}

export interface ISelfSignupSocialConnectSettingsServer {
  signup_sc_mode: SocialConnectMode;
  signup_sc_reminder_enabled: boolean;
  signup_sc_reminder_subject: string;
  signup_sc_reminder_body: string;
  signup_sc_reminder_interval_days: number;
}

export interface ISelfSignupSocialConnectSettings {
  linkedinMode: SocialConnectMode;
  reminderEnabled: boolean;
  reminderSubject: string;
  reminderBody: string;
  reminderIntervalDays: number;
}

export interface ISelfSignupSocialConnectPayload {
  signup_sc_mode: SocialConnectMode;
  signup_sc_reminder_enabled: boolean;
  signup_sc_reminder_subject: string;
  signup_sc_reminder_body: string;
  signup_sc_reminder_interval_days: number;
}

export interface ISignupServer {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_country_code: string;
  user_country_code: string;
  state: string;
  redirect_sc_url: string;
  created_at: string;
}
